import { useRef, useEffect } from 'react';

// Adopted from: https://github.com/foray1010/use-typed-event-listener
function useEventListener<WindowEvent extends keyof WindowEventMap>(
  eventName: WindowEvent,
  handler: (event: WindowEventMap[WindowEvent]) => void,
  element?: Window,
  options?: AddEventListenerOptions,
): void;
function useEventListener<DocumentEvent extends keyof DocumentEventMap>(
  eventName: DocumentEvent,
  handler: (event: DocumentEventMap[DocumentEvent]) => void,
  element?: Document,
  options?: AddEventListenerOptions,
): void;
function useEventListener<HTMLElementEvent extends keyof HTMLElementEventMap>(
  eventName: HTMLElementEvent,
  handler: (event: HTMLElementEventMap[HTMLElementEvent]) => void,
  element?: HTMLElement,
  options?: AddEventListenerOptions,
): void;
function useEventListener(
  eventName: string,
  handler: (event: Event) => void,
  element?: EventTarget,
  options?: AddEventListenerOptions,
): void;

function useEventListener<
  WindowEvent extends keyof WindowEventMap,
  DocumentEvent extends keyof DocumentEventMap,
  HTMLElementEvent extends keyof HTMLElementEventMap,
>(
  eventName: WindowEvent | DocumentEvent | HTMLElementEvent | string,
  handler: (
    event:
      | WindowEventMap[WindowEvent]
      | DocumentEventMap[DocumentEvent]
      | HTMLElementEventMap[HTMLElementEvent]
      | Event,
  ) => void,
  element: EventTarget = global,
  options?: AddEventListenerOptions,
) {
  const savedHandler = useRef(handler);
  savedHandler.current = handler;
  const { capture, passive, once } = options ?? {};

  useEffect(() => {
    const isSupported = !!element?.addEventListener;
    if (!isSupported) {
      return;
    }

    const eventListener: typeof savedHandler.current = (event) => savedHandler.current(event);
    const opts = { capture, passive, once };
    element.addEventListener(eventName, eventListener, opts);
    return () => {
      element.removeEventListener(eventName, eventListener, opts);
    };
  }, [eventName, element, capture, passive, once, savedHandler]);
}

export default useEventListener;
